// Mitarbeiter-Bericht — one person's clients, calls/entries and
// appointments for a period, in the layout of the list Vivien asked for
// (2026-11-04: Marianne Andjelkovic). Print opens an A4-landscape page
// the browser can save as PDF; CSV opens in Excel. A note added from a
// row lands in the client's Notizen tab, so call results stop living on
// paper.

import { createFileRoute, redirect } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { FileDown, Printer, Plus, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SearchablePicker } from '@/components/ui/SearchablePicker';
import { useAgents } from '@/hooks/data/useAgents';
import { useAddReportNote, useStaffReport, type StaffReportRow } from '@/hooks/data/useStaffReport';
import { fetchMe } from '@/lib/auth';
import { formatDate, formatDateTime } from '@/lib/formatLocale';
import { activeDirection } from '@/i18n';
import { buildCsv, buildPrintHtml, type ExportLabels, type ExportRow } from '@/lib/staffReportExport';

export const Route = createFileRoute('/_app/admin/staff-report')({
  beforeLoad: async () => {
    const me = await fetchMe();
    if (!me || me.profile?.role !== 'admin') {
      throw redirect({ to: '/dashboard' });
    }
  },
  component: StaffReportPage,
});

const DAY = 24 * 60 * 60 * 1000;

/** yyyy-mm-dd in local time, for <input type="date">. */
function toInputDate(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
/** Start of the local day for a yyyy-mm-dd value. */
function fromInputDate(v: string): number {
  const [y, m, d] = v.split('-').map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1).getTime();
}

function StaffReportPage() {
  const { t, i18n } = useTranslation();
  const { data: agents = [] } = useAgents();
  const [profileId, setProfileId] = useState('');
  const [fromStr, setFromStr] = useState(() => toInputDate(Date.now() - 30 * DAY));
  const [toStr, setToStr] = useState(() => toInputDate(Date.now()));

  // „bis" is inclusive on screen, so the query runs to the start of the NEXT day.
  const fromMs = fromStr ? fromInputDate(fromStr) : 0;
  const toMs = toStr ? fromInputDate(toStr) + DAY : 0;
  const rangeValid = !!fromStr && !!toStr && fromMs < toMs;

  const { data: report, isLoading, error } = useStaffReport(profileId, fromMs, toMs);

  const options = useMemo(
    () =>
      // Inactive people stay pickable: a report about someone who has left
      // (Marianne was already inactive when Vivien asked) is a normal ask.
      [...agents]
        .sort(
          (a, b) =>
            Number(a.status !== 'active') - Number(b.status !== 'active') ||
            a.full_name.localeCompare(b.full_name),
        )
        .map((a) => ({
          value: a.id,
          label: a.full_name,
          secondary: [
            t(`staff_report.role.${a.role}`, a.role),
            a.agent_id,
            a.status !== 'active' ? t('staff_report.inactive', 'inaktiv') : null,
          ]
            .filter(Boolean)
            .join(' · '),
        })),
    [agents, t],
  );

  const enumLabel = (group: string, v: string | null) => (v ? t(`enums.${group}.${v}`, v) : '');

  const toExportRow = (r: StaffReportRow): ExportRow => ({
    client: [r.title, r.first_name, r.last_name].filter(Boolean).join(' '),
    type: enumLabel('client_type', r.client_type),
    phone: r.phone ?? '',
    makler: r.makler_name ?? '',
    enteredBy:
      r.entry_kind === 'client'
        ? (report?.person.full_name ?? '')
        : `${report?.person.full_name ?? ''} (${t('staff_report.entry_appointment', 'Termin')})`,
    enteredAt: formatDateTime(r.entered_at_ms),
    apptAt: r.appointment_date_ms != null ? formatDateTime(r.appointment_date_ms) : '',
    apptType: enumLabel('appointment_type', r.appointment_type),
    apptStatus: enumLabel('appointment_status', r.appointment_status),
    note: r.last_note
      ? `${r.last_note_date_ms != null ? formatDate(r.last_note_date_ms) + ': ' : ''}${r.last_note}`
      : '',
  });

  const columns: ExportLabels['columns'] = [
    t('staff_report.col.client', 'Klient (Vor- und Nachname)'),
    t('staff_report.col.type', 'Käufer / Verkäufer'),
    t('staff_report.col.phone', 'Telefon'),
    t('staff_report.col.makler', 'Makler (für wen)'),
    t('staff_report.col.entered_by', 'Wer hat angerufen und eingetragen'),
    t('staff_report.col.entered_at', 'Datum (Anruf / Eintrag)'),
    t('staff_report.col.appt_at', 'Termin am'),
    t('staff_report.col.appt_type', 'Terminart'),
    t('staff_report.col.appt_status', 'Terminstatus'),
    t('staff_report.col.note', 'Letzte Notiz'),
  ];

  const periodText = `${formatDate(fromMs)} – ${formatDate(toMs - 1)}`;

  const onPrint = () => {
    if (!report) return;
    const html = buildPrintHtml(
      report.rows.map(toExportRow),
      {
        confidential: t('staff_report.confidential', 'VERTRAULICH'),
        title: t('staff_report.print_title', '{{name}} – Klienten, Anrufe/Einträge und Termine', {
          name: report.person.full_name,
        }),
        period: t('staff_report.period', 'Zeitraum: {{period}}', { period: periodText }),
        generated: t('staff_report.generated', 'Stand {{date}}', { date: formatDate(Date.now()) }),
        source: t('staff_report.source', 'Quelle: 4M-CRM'),
        columns,
        empty: t('staff_report.empty', 'Keine Einträge in diesem Zeitraum.'),
        page: t('staff_report.page', 'Seite'),
      },
      { lang: i18n.language, dir: activeDirection(), personName: report.person.full_name },
    );
    const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
    const w = window.open(url, '_blank');
    if (!w) toast.error(t('staff_report.popup_blocked', 'Das Druckfenster wurde vom Browser blockiert. Bitte Pop-ups für diese Seite erlauben.'));
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const onCsv = () => {
    if (!report) return;
    const csv = buildCsv(report.rows.map(toExportRow), columns);
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    const safeName = report.person.full_name.replace(/[^\p{L}\p{N}]+/gu, '_');
    a.href = url;
    a.download = `4M_Bericht_${safeName}_${fromStr}_${toStr}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  };

  return (
    <div className="space-y-5">
      <header>
        <h1 className="font-display text-2xl text-ink">{t('staff_report.title', 'Mitarbeiter-Bericht')}</h1>
        <p className="mt-1 text-sm text-ink-3">
          {t(
            'staff_report.subtitle',
            'Alle Klienten, die eine Person in einem Zeitraum eingetragen oder für die sie einen Termin gebucht hat – mit Termin und letzter Notiz.',
          )}
        </p>
      </header>

      <div className="fm-card grid gap-3 rounded-xl border border-line bg-paper p-4 md:grid-cols-[minmax(0,2fr)_1fr_1fr_auto] md:items-end">
        <label className="block text-sm">
          <span className="mb-1 block text-ink-3">{t('staff_report.person', 'Mitarbeiter/in')}</span>
          <SearchablePicker
            value={profileId}
            onChange={setProfileId}
            options={options}
            placeholder={t('staff_report.pick_person', 'Person wählen …')}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-ink-3">{t('staff_report.from', 'Von')}</span>
          <input
            type="date"
            value={fromStr}
            onChange={(e) => setFromStr(e.target.value)}
            className="h-10 w-full rounded-md border border-line bg-paper px-3"
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-ink-3">{t('staff_report.to', 'Bis')}</span>
          <input
            type="date"
            value={toStr}
            onChange={(e) => setToStr(e.target.value)}
            className="h-10 w-full rounded-md border border-line bg-paper px-3"
          />
        </label>
        <div className="flex gap-2">
          <Button onClick={onPrint} disabled={!report}>
            <Printer className="me-1.5 h-4 w-4" />
            {t('staff_report.print', 'Drucken / PDF')}
          </Button>
          <Button variant="outline" onClick={onCsv} disabled={!report}>
            <FileDown className="me-1.5 h-4 w-4" />
            CSV
          </Button>
        </div>
      </div>

      {!rangeValid && (
        <p className="text-sm text-destructive">{t('staff_report.invalid_range', '„Von" muss vor „Bis" liegen.')}</p>
      )}

      {!profileId && (
        <p className="rounded-xl border border-dashed border-line p-8 text-center text-sm text-ink-3">
          {t('staff_report.choose_first', 'Wählen Sie eine Person, um den Bericht zu sehen.')}
        </p>
      )}

      {profileId && isLoading && <p className="text-sm text-ink-3">{t('common.loading', 'Laden …')}</p>}
      {error && <p className="text-sm text-destructive">{t('staff_report.load_failed', 'Der Bericht konnte nicht geladen werden.')}</p>}

      {report && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Kpi label={t('staff_report.kpi.rows', 'Klienten im Bericht')} value={report.totals.rows} />
            <Kpi label={t('staff_report.kpi.created', 'Selbst eingetragen')} value={report.totals.created_clients} />
            <Kpi label={t('staff_report.kpi.booked', 'Termine gebucht')} value={report.totals.booked_appointments} />
            <Kpi label={t('staff_report.kpi.with_appt', 'Mit Termin')} value={report.totals.with_appointment} />
          </div>

          {report.truncated && (
            <p className="flex items-center gap-2 rounded-lg bg-[hsl(var(--warning)/0.1)] p-3 text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {t('staff_report.truncated', 'Nur die ersten 2.000 Klienten werden angezeigt. Bitte den Zeitraum verkleinern.')}
            </p>
          )}

          <div className="fm-table-wrap rounded-xl border border-line">
            <table className="w-full min-w-[1100px] text-sm">
              <thead className="bg-paper-2 text-start text-xs uppercase tracking-wide text-ink-3">
                <tr>
                  {columns.map((c) => (
                    <th key={c} className="px-3 py-2 text-start font-medium">
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.rows.length === 0 && (
                  <tr>
                    <td colSpan={10} className="px-3 py-8 text-center text-ink-3">
                      {t('staff_report.empty', 'Keine Einträge in diesem Zeitraum.')}
                    </td>
                  </tr>
                )}
                {report.rows.map((r) => (
                  <ReportRow key={r.client_id} row={r} cells={toExportRow(r)} />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: number }) {
  return (
    <div className="fm-kpi rounded-xl border border-line bg-paper p-3">
      <div className="text-xs text-ink-3">{label}</div>
      <div className="font-display text-2xl text-ink">{value.toLocaleString()}</div>
    </div>
  );
}

function ReportRow({ row, cells }: { row: StaffReportRow; cells: ExportRow }) {
  const { t } = useTranslation();
  const addNote = useAddReportNote();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');

  const save = async () => {
    const body = draft.trim();
    if (!body) return;
    try {
      await addNote.mutateAsync({ clientId: row.client_id, body });
      setDraft('');
      setOpen(false);
      toast.success(t('staff_report.note_saved', 'Notiz gespeichert.'));
    } catch {
      toast.error(t('staff_report.note_failed', 'Notiz konnte nicht gespeichert werden.'));
    }
  };

  return (
    <tr className="border-t border-line align-top">
      <td className="px-3 py-2 font-medium text-ink">{cells.client}</td>
      <td className="px-3 py-2">{cells.type}</td>
      <td className="px-3 py-2 whitespace-nowrap font-mono text-xs" dir="ltr">
        {cells.phone}
      </td>
      <td className="px-3 py-2">{cells.makler || '—'}</td>
      <td className="px-3 py-2">{cells.enteredBy}</td>
      <td className="px-3 py-2 whitespace-nowrap">{cells.enteredAt}</td>
      <td className="px-3 py-2 whitespace-nowrap">{cells.apptAt || '—'}</td>
      <td className="px-3 py-2">{cells.apptType}</td>
      <td className="px-3 py-2">{cells.apptStatus}</td>
      <td className="max-w-[280px] px-3 py-2">
        {cells.note && <p className="mb-1 whitespace-pre-wrap text-ink-2">{cells.note}</p>}
        {open ? (
          <div className="space-y-1.5">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={2}
              autoFocus
              placeholder={t('staff_report.note_placeholder', 'z. B. „Verkauft", „Morgen wird angerufen" …')}
              className="w-full rounded-md border border-line bg-paper px-2 py-1 text-sm"
            />
            <div className="flex gap-1.5">
              <Button size="sm" onClick={save} disabled={!draft.trim() || addNote.isPending}>
                {t('common.save', 'Speichern')}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
                {t('common.cancel', 'Abbrechen')}
              </Button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-1 text-xs text-ink-3 hover:text-ink"
          >
            <Plus className="h-3.5 w-3.5" />
            {t('staff_report.add_note', 'Notiz')}
          </button>
        )}
      </td>
    </tr>
  );
}
